"use client";
import { useCallback, useEffect, useState } from "react"
import { NodeSelection } from "@tiptap/pm/state"

// --- Hooks ---
import { useTiptapEditor } from "@/tiptap/hooks/use-tiptap-editor"

// --- Lib ---
import { isExtensionAvailable } from "@/tiptap/lib/tiptap-utils"

// --- Icons ---
import { ImageInlineIcon } from "@/tiptap/icons/image-inline-icon"
import { ImageBlockIcon } from "@/tiptap/icons/image-block-icon"

export const BLOCK_IMAGE_NODE = "image"
export const INLINE_IMAGE_NODE = "inlineImage"

// Only these survive a conversion. Caption, alignment, border styling and
// editor-specific style attrs belong to the block node and are dropped.
const SHARED_IMAGE_ATTRS = ["src", "alt", "title", "width", "height"]

function sharedAttrs(node) {
  return SHARED_IMAGE_ATTRS.reduce((attrs, key) => {
    const value = node.attrs[key]
    if (value !== null && value !== undefined && value !== "") attrs[key] = value
    return attrs
  }, {})
}

function findImageElement(domNode) {
  if (!domNode) return null
  if (domNode.nodeName === "IMG") return domNode
  return domNode.querySelector?.("img") ?? null
}

/**
 * Reads the currently rendered size of an image node from the editor DOM.
 * Used when converting an unsized image so it doesn't fall through to the
 * icon-sized CSS fallback (`max-height: 1.5em` on unsized `img[data-inline]`).
 */
export function snapshotRenderedImageSize(view, pos) {
  try {
    const img = findImageElement(view.nodeDOM(pos))
    if (!img) return null

    // Use the border-box size (matches inline resize) so the 0.125rem
    // transparent/selected border on img[data-inline] is not dropped.
    const rect = img.getBoundingClientRect?.()
    const width = Math.round(rect?.width || img.naturalWidth || 0)
    const height = Math.round(rect?.height || img.naturalHeight || 0)
    if (width <= 0 && height <= 0) return null

    const size = {}
    if (width > 0) size.width = width
    if (height > 0) size.height = height
    return size
  } catch {
    return null
  }
}

function attrsForConversion(node, view, pos) {
  const attrs = sharedAttrs(node)
  if (attrs.width || attrs.height) return attrs

  const size = snapshotRenderedImageSize(view, pos)
  if (!size) return attrs
  return { ...attrs, ...size }
}

function selectedImageNode(editor) {
  const { selection } = editor.state
  if (!(selection instanceof NodeSelection)) return null

  const typeName = selection.node.type.name
  if (typeName !== BLOCK_IMAGE_NODE && typeName !== INLINE_IMAGE_NODE) return null

  return { node: selection.node, pos: selection.from }
}

/**
 * "block" when a block image is selected, "inline" when an inline image is
 * selected, null otherwise.
 */
export function getImageInlineMode(editor) {
  if (!editor) return null

  const selected = selectedImageNode(editor)
  if (!selected) return null

  return selected.node.type.name === INLINE_IMAGE_NODE ? "inline" : "block"
}

export function canToggleImageInline(editor) {
  if (!editor || !editor.isEditable) return false
  if (!isExtensionAvailable(editor, [BLOCK_IMAGE_NODE])) return false
  if (!isExtensionAvailable(editor, [INLINE_IMAGE_NODE])) return false

  return getImageInlineMode(editor) !== null
}

function blockToInline(editor, node, pos) {
  const { state, view } = editor
  const inlineType = state.schema.nodes[INLINE_IMAGE_NODE]
  const paragraphType = state.schema.nodes.paragraph
  if (!inlineType || !paragraphType) return false

  const inlineImage = inlineType.create(attrsForConversion(node, view, pos))
  const paragraph = paragraphType.create(null, inlineImage)

  const tr = state.tr.replaceWith(pos, pos + node.nodeSize, paragraph)
  // The atom is the paragraph's first child, one position inside it.
  tr.setSelection(NodeSelection.create(tr.doc, pos + 1))
  view.dispatch(tr.scrollIntoView())
  return true
}

function inlineToBlock(editor, node, pos) {
  const { state, view } = editor
  const blockType = state.schema.nodes[BLOCK_IMAGE_NODE]
  if (!blockType) return false

  const blockImage = blockType.create(attrsForConversion(node, view, pos))
  const $pos = state.doc.resolve(pos)
  const parent = $pos.parent

  let tr
  let blockPos
  if (
    parent.childCount === 1 &&
    $pos.node($pos.depth - 1).canReplaceWith(
      $pos.index($pos.depth - 1),
      $pos.index($pos.depth - 1) + 1,
      blockType,
    )
  ) {
    // The paragraph held nothing but the atom: swap the whole paragraph so no
    // empty paragraph is left behind.
    blockPos = $pos.before()
    tr = state.tr.replaceWith(blockPos, $pos.after(), blockImage)
  } else {
    // Inside text: replaceWith splits the paragraph around the block node.
    tr = state.tr.replaceWith(pos, pos + node.nodeSize, blockImage)
    blockPos = findInsertedBlockImage(tr)
  }

  if (blockPos === null) return false

  tr.setSelection(NodeSelection.create(tr.doc, blockPos))
  view.dispatch(tr.scrollIntoView())
  return true
}

// After splitting a paragraph the block image's position shifts by the
// closing/opening tokens, so read it back from the range the replace step
// inserted. A nearby pre-existing image with the same src is never in that
// range, so it can't be picked by mistake.
function findInsertedBlockImage(tr) {
  const step = tr.steps[tr.steps.length - 1]
  let found = null
  step.getMap().forEach((_oldStart, _oldEnd, newStart, newEnd) => {
    if (found !== null) return
    tr.doc.nodesBetween(newStart, newEnd, (child, childPos) => {
      if (found !== null) return false
      if (child.type.name === BLOCK_IMAGE_NODE) {
        found = childPos
        return false
      }
      return true
    })
  })
  return found
}

/**
 * Converts the selected block image to an inline image, or the selected
 * inline image to a block image, keeping the same src.
 */
export function toggleImageInline(editor) {
  if (!canToggleImageInline(editor)) return false

  const selected = selectedImageNode(editor)
  if (!selected) return false

  const { node, pos } = selected
  return node.type.name === INLINE_IMAGE_NODE
    ? inlineToBlock(editor, node, pos)
    : blockToInline(editor, node, pos)
}

export function shouldShowImageInlineToggle(props) {
  const { editor, hideWhenUnavailable } = props

  if (!editor) return false

  if (!hideWhenUnavailable) {
    return true
  }

  return canToggleImageInline(editor)
}

export function useImageInlineToggle(config) {
  const {
    editor: providedEditor,
    hideWhenUnavailable = false,
    onToggled,
  } = config || {}

  const { editor } = useTiptapEditor(providedEditor)
  const [isVisible, setIsVisible] = useState(true)
  const [mode, setMode] = useState(() => getImageInlineMode(editor))
  const canToggle = canToggleImageInline(editor)

  useEffect(() => {
    if (!editor) return

    const handleUpdate = () => {
      setIsVisible(shouldShowImageInlineToggle({ editor, hideWhenUnavailable }))
      setMode(getImageInlineMode(editor))
    }

    handleUpdate()

    editor.on("selectionUpdate", handleUpdate)
    editor.on("transaction", handleUpdate)

    return () => {
      editor.off("selectionUpdate", handleUpdate)
      editor.off("transaction", handleUpdate)
    };
  }, [editor, hideWhenUnavailable])

  const handleToggle = useCallback(() => {
    if (!editor) return false

    const previousMode = getImageInlineMode(editor)
    const success = toggleImageInline(editor)
    if (success) {
      onToggled?.(previousMode === "inline" ? "block" : "inline")
    }
    return success
  }, [editor, onToggled])

  const isInline = mode === "inline"

  return {
    isVisible,
    canToggle,
    mode,
    handleToggle,
    label: isInline ? "Make block image" : "Make inline image",
    Icon: isInline ? ImageBlockIcon : ImageInlineIcon,
  }
}
